"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
// Close icon will be rendered via ButtonIcon

import { cn } from "@/lib/utils"
import styles from './dialog.module.css'
import { ButtonIcon } from '@/components/button/buttonIcon/buttonIcon'
import type { IconName } from '@/components/icon/icon'

type DialogRootProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>
const Dialog = ({ modal = true, ...props }: DialogRootProps) => (
  <DialogPrimitive.Root modal={modal} {...props} />
)

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(styles.overlay, className)}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

type DialogSize = 'sm' | 'md' | 'lg' | 'xl'

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    size?: DialogSize
    noOverlay?: boolean
    title?: React.ReactNode
    description?: React.ReactNode
    showIconLeft?: boolean
    leftIconName?: IconName
    showIconRight?: boolean
    rightIconName?: IconName
    layerOffset?: number
    align?: 'left' | 'center'
    onIconLeftClick?: () => void
    /** When true, mobile dialog peeks from bottom with 24px top margin and rounded top corners instead of going full-screen. */
    peeking?: boolean
    /** When provided, replaces the title text with a Select dropdown. */
    titleSelect?: React.ReactNode
    /** Position of the title text within the header. */
    titlePosition?: 'left' | 'center'
    /** Size of the built-in header title. `md` (default) uses `body-lg`; `lg` uses `title-lg` in a taller header. */
    titleSize?: 'md' | 'lg'
  }
>((
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- `description` is destructured to keep it out of the spread `...props` passed to Radix (it would be rejected as an invalid HTML attribute); not consumed in JSX.
  { className, children, size = 'md', noOverlay = false, title, description, showIconLeft = false, leftIconName = 'ChevronLeft', showIconRight = true, rightIconName = 'X', layerOffset = 0, style, align = 'left', onIconLeftClick, peeking = false, titleSelect, titlePosition = 'left', titleSize = 'md', ...props },
  ref,
) => {
  const overlayStyle = layerOffset
    ? { zIndex: 90 + layerOffset }
    : undefined

  const contentStyle: React.CSSProperties = {
    ...(style || {}),
    zIndex: style?.zIndex ?? (91 + layerOffset),
  }

  // If a title or left icon is provided, we render a built-in header row.
  // Otherwise, we assume the caller will use <DialogHeader> manually and
  // we only render the close icon floated on the content container.
  const hasBuiltInHeader = !!title || !!titleSelect || showIconLeft

  return (
    <DialogPortal>
      {!noOverlay && <DialogOverlay style={overlayStyle} />}
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          styles.content,
          size === 'sm' && styles['size-sm'],
          size === 'md' && styles['size-md'],
          size === 'lg' && styles['size-lg'],
          size === 'xl' && styles['size-xl'],
          align === 'center' && styles['align-center'],
          peeking && styles.peeking,
          className,
        )}
        style={contentStyle}
        // Radix dev-only warning: every DialogContent must have either a
        // <Description /> child OR `aria-describedby={undefined}` set explicitly.
        // Most dialogs in this app use the title as the descriptive label and
        // don't need a separate body description, so we suppress at the shared
        // component level. Callers that DO want a real description can pass
        // their own `aria-describedby` via {...props} to point at an element id.
        // (This is a no-op when the caller already provided aria-describedby.)
        aria-describedby={(props as Record<string, unknown>)['aria-describedby'] as string | undefined}
        {...props}
      >
        {hasBuiltInHeader ? (
          <div className={cn(styles.header, showIconLeft && styles.headerHasLeft, titlePosition === 'center' && styles.headerCenter, titleSize === 'lg' && styles.headerLg)}>
            {showIconLeft ? (
              <ButtonIcon
                className={cn(styles.left)}
                size="md"
                border={false}
                name={leftIconName}
                aria-label="Back"
                onClick={onIconLeftClick}
              />
            ) : null}
            {titleSelect ? (
              <>
                <DialogPrimitive.Title className={styles.srOnly}>Dialog</DialogPrimitive.Title>
                <div className={styles.titleSelect}>{titleSelect}</div>
              </>
            ) : title ? (
              <DialogPrimitive.Title asChild>
                <h3 className={cn(titleSize === 'lg' ? 'title-lg' : 'body-lg', styles.title)}>{title}</h3>
              </DialogPrimitive.Title>
            ) : null}
            {showIconRight ? (
              <DialogPrimitive.Close asChild>
                <ButtonIcon
                  className={cn(styles.close)}
                  size="md"
                  border={false}
                  name={rightIconName}
                  aria-label="Close"
                />
              </DialogPrimitive.Close>
            ) : null}
          </div>
        ) : null}
        {children}
        {!hasBuiltInHeader && showIconRight ? (
          <DialogPrimitive.Close asChild>
            <ButtonIcon
              className={cn(styles.close)}
              size="sm"
              border={false}
              name={rightIconName}
              aria-label="Close"
            />
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn(styles.header, className)} {...props} />
)
DialogHeader.displayName = "DialogHeader"

interface DialogFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  pinnedBottom?: boolean
}

const DialogFooter = ({ className, pinnedBottom = true, ...props }: DialogFooterProps) => (
  <div
    className={cn(styles.footer, pinnedBottom ? styles.footerPinned : styles.footerUnpinned, className)}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title ref={ref} className={cn('body-lg', styles.title, className)} {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn('body-md', styles.description, className)} {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
